import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Smartphone } from "lucide-react";

export default function ProductCard({ p }) {
  const soldOut = p.availability === "sold_out";
  const onSale = !!p.on_sale && !soldOut;
  const showSalePrice = onSale && p.sale_price && p.sale_price < p.price;

  // Build de-duplicated image list, prioritising the primary `image` field.
  const allImages = [p.image, ...(Array.isArray(p.images) ? p.images : [])]
    .filter(Boolean)
    .filter((url, i, arr) => arr.indexOf(url) === i);
  const [activeImg, setActiveImg] = useState(0);
  const hasMulti = allImages.length > 1;

  return (
    <Link to={`/produkt/${p.sku}`} className="group block" data-testid={`product-card-${p.sku}`}>
      <div className="aspect-square rounded-2xl bg-[#141417] border border-white/5 overflow-hidden mb-4 relative group-hover:border-white/15 transition-colors">
        {allImages.map((url, idx) => (
          <img
            key={idx}
            src={url}
            alt={p.name}
            className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-500 ${idx === activeImg ? "opacity-100" : "opacity-0"} ${soldOut ? "opacity-55 grayscale" : ""}`}
            onError={(e) => { e.target.style.opacity = 0; }}
            loading={idx === 0 ? "eager" : "lazy"}
          />
        ))}

        {/* Image-dot indicators — only when >1 image */}
        {hasMulti && (
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex gap-1 bg-black/55 backdrop-blur-md px-2 py-1.5 rounded-full border border-white/10 z-10" data-testid={`image-dots-${p.sku}`}>
            {allImages.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onMouseEnter={() => setActiveImg(idx)}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveImg(idx); }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  idx === activeImg ? "bg-white w-4" : "bg-white/45 hover:bg-white/80"
                }`}
                aria-label={`Bild ${idx + 1}`}
                data-testid={`image-dot-${p.sku}-${idx}`}
              />
            ))}
          </div>
        )}

        {/* Badge priority: Sold-Out > SALE > Bestseller */}
        {soldOut ? (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-amber-500 text-black text-[10px] uppercase tracking-wider font-bold z-10" data-testid={`sold-out-${p.sku}`}>
            Ausverkauft
          </div>
        ) : onSale ? (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#DC1928] text-white text-[10px] uppercase tracking-wider font-bold shadow-lg z-10" data-testid={`sale-${p.sku}`}>
            {showSalePrice ? `−${Math.round(((p.price - p.sale_price) / p.price) * 100)}% SALE` : "SALE"}
          </div>
        ) : p.featured ? (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#DC1928] text-white text-[10px] uppercase tracking-wider font-bold z-10">
            Bestseller
          </div>
        ) : null}
      </div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{p.brand}</div>
      <h3 className="font-display text-base font-medium text-white mt-1 group-hover:text-[#FF2D40] transition-colors line-clamp-1">{p.name}</h3>
      <CompatibilityChip product={p} />
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="text-xs text-zinc-500">{p.rating}</span>
        </div>
        <div className={`flex items-baseline gap-2 ${soldOut ? "opacity-60" : ""}`}>
          {showSalePrice && (
            <span className="text-xs text-zinc-500 line-through numeric">€{p.price.toFixed(2)}</span>
          )}
          <span className={`font-display font-semibold numeric ${soldOut ? "text-zinc-500 line-through" : showSalePrice ? "text-[#FF2D40]" : "text-white"}`}>
            €{(showSalePrice ? p.sale_price : p.price).toFixed(2)}
          </span>
        </div>
      </div>
    </Link>
  );
}

/**
 * Compact "Kompatibel mit ..."-chip shown below the product title on cards.
 * Uses `compatible_models` array if present, otherwise falls back to the
 * legacy `compatible` string. Stays hidden if no compat data exists.
 */
function CompatibilityChip({ product }) {
  const models = Array.isArray(product.compatible_models)
    ? product.compatible_models.filter(Boolean)
    : [];
  const legacy = (product.compatible || "").trim();

  if (models.length === 0 && !legacy) return null;

  // Build short label
  let label;
  let count = 0;
  if (models.length > 0) {
    count = models.length;
    if (models.length === 1) {
      label = models[0];
    } else if (models.length === 2) {
      label = `${models[0]} + ${models[1]}`;
    } else {
      label = `${models[0]} +${models.length - 1}`;
    }
  } else {
    label = legacy;
  }

  return (
    <div
      className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 max-w-full"
      data-testid={`compat-chip-${product.sku}`}
      title={models.length > 0 ? `Kompatibel mit: ${models.join(", ")}` : `Kompatibel: ${legacy}`}
    >
      <Smartphone className="w-2.5 h-2.5 text-[#FF4F5E] shrink-0" />
      <span className="text-[10px] text-zinc-400 truncate">
        Kompatibel: <span className="text-zinc-200 font-medium">{label}</span>
        {count > 2 && <span className="text-zinc-500"> Modelle</span>}
      </span>
    </div>
  );
}
